import React, { useMemo } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import { useEvents } from '@/contexts/EventsContext';

const SalonReviews: React.FC = () => {
    const { reviews } = useEvents();
    const [visibleReviews, setVisibleReviews] = React.useState(5);

    const formatTimeAgo = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 1) return '1 giorno fa';
        if (diffDays <= 6) return `${diffDays} giorni fa`;
        if (diffDays <= 13) return '1 settimana fa';
        if (diffDays <= 20) return '2 settimane fa';
        if (diffDays <= 29) return '3 settimane fa';

        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'mese' : 'mesi'} fa`;

        const diffYears = Math.floor(diffDays / 365);
        if (diffYears === 1) return '1 anno fa';

        return 'più di 1 anno fa';
    };

    const averageRating = useMemo(() => reviews.length > 0 ? reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length : 0, [reviews]);

    const AVATAR_COLORS = [
        'bg-pink-100 text-pink-600',
        'bg-purple-100 text-purple-600',
        'bg-indigo-100 text-indigo-600',
        'bg-blue-100 text-blue-600',
        'bg-teal-100 text-teal-600'
    ];

    const displayedReviews = reviews.slice(0, visibleReviews);

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center flex flex-col items-center justify-center">
                <h2 className="text-6xl font-bold text-gray-900 mb-2">{averageRating.toFixed(1)}</h2>
                <div className="flex gap-1 mb-4 text-yellow-400">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} fill="currentColor" size={24} className={i > Math.round(averageRating) ? 'text-gray-200' : ''} />)}
                </div>
                <p className="text-gray-500 font-medium">{reviews.length === 0 ? 'Nessuna recensione' : `Basato su ${reviews.length} recensioni`}</p>
            </div>
            <div className="md:col-span-2 space-y-4">
                {reviews.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-2xl">
                        <MessageSquare size={24} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-400 text-sm">Non ci sono ancora recensioni per questo salone.</p>
                    </div>
                ) : (
                    <>
                        {displayedReviews.map((review, index) => (
                            <div key={review.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}>
                                            {review.author ? review.author[0].toUpperCase() : '?'}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 text-sm">{review.author}</h4>
                                            <p className="text-xs text-gray-400">{formatTimeAgo(review.created_at)}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-0.5 text-yellow-400">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill={i < review.rating ? "currentColor" : "none"} className={i >= review.rating ? "text-gray-200" : ""} />)}</div>
                                </div>
                                {review.comment && <p className="text-gray-600 text-sm leading-relaxed mt-3">{review.comment}</p>}
                            </div>
                        ))}

                        {visibleReviews < reviews.length && (
                            <div className="text-center pt-2">
                                <button
                                    onClick={() => setVisibleReviews(prev => prev + 5)}
                                    className="px-6 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors text-sm"
                                >
                                    Mostra altre recensioni
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SalonReviews;